import { STORAGE_KEY as USER_GUIDES_KEY } from "./userGuideStorage";
import { disconnectNotificationSocket } from "./notificationSocket";

export const GOOGLE_AUTH_PENDING_KEY = "gc_google_auth_pending";
export const GOOGLE_AUTH_PENDING_VIEW_KEY = "gc_google_auth_pending_view";
export const NOSUGAR_SESSION_KEY = "gc_nosugar_session";
export const AUTH_BOOTSTRAP_QUERY_KEY = "gc_auth";

function utf8ToBase64(value) {
  if (!value || typeof window === "undefined") {
    return "";
  }

  try {
    return window.btoa(unescape(encodeURIComponent(value)));
  } catch (e) {
    return "";
  }
}

function base64ToUtf8(value) {
  if (!value || typeof window === "undefined") {
    return "";
  }

  try {
    return decodeURIComponent(escape(window.atob(value)));
  } catch (e) {
    return "";
  }
}

export function isGoogleAuthPending() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  return (
    window.localStorage.getItem(GOOGLE_AUTH_PENDING_KEY) === "1" &&
    !!window.localStorage.getItem("t_k")
  );
}

export function getGoogleAuthPendingView() {
  if (typeof window === "undefined" || !window.localStorage) {
    return "";
  }
  return window.localStorage.getItem(GOOGLE_AUTH_PENDING_VIEW_KEY) || "";
}

export function setGoogleAuthPending(view) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  window.localStorage.setItem(GOOGLE_AUTH_PENDING_KEY, "1");
  if (view) {
    window.localStorage.setItem(GOOGLE_AUTH_PENDING_VIEW_KEY, view);
  }
}

export function clearGoogleAuthPending() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  window.localStorage.removeItem(GOOGLE_AUTH_PENDING_KEY);
  window.localStorage.removeItem(GOOGLE_AUTH_PENDING_VIEW_KEY);
}

export function setNosugarSessionFlag(enabled = true) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  if (enabled) {
    window.localStorage.setItem(NOSUGAR_SESSION_KEY, "1");
    return;
  }

  window.localStorage.removeItem(NOSUGAR_SESSION_KEY);
}

export function hasNosugarSessionFlag() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }

  return window.localStorage.getItem(NOSUGAR_SESSION_KEY) === "1";
}

// 清除除了新手引导状态以外的所有 localStorage 数据
export function clearLocalStorageExceptUserGuides() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  disconnectNotificationSocket();
  try {
    const ls = window.localStorage;
    const keepValue = ls.getItem(USER_GUIDES_KEY);
    const redirectUrl = ls.getItem("redirectUrl");
    // 先拿到所有 key，避免遍历过程中长度变化问题
    const keys = [];
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (key !== null) {
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      if (key !== USER_GUIDES_KEY && key !== "redirectUrl") {
        ls.removeItem(key);
      }
    });
    // 如果原来没有新手引导 key，这里不会新建它
    if (keepValue !== null) {
      ls.setItem(USER_GUIDES_KEY, keepValue);
    }
    // 如果原来有 redirectUrl，恢复它
    if (redirectUrl !== null) {
      ls.setItem("redirectUrl", redirectUrl);
    }
  } catch (e) {
    // 忽略清理异常
  }
}

// 清除除了站点配置以外的所有 sessionStorage 数据
export function clearSessionStorageExceptSiteConfig() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const ss = window.sessionStorage;
    const siteConfigEntries = {};
    // 先拿到所有 key，避免遍历过程中长度变化问题
    const keys = [];
    for (let i = 0; i < ss.length; i++) {
      const key = ss.key(i);
      if (key !== null) {
        if (key === "siteConfig" || key.indexOf("siteConfig:") === 0) {
          siteConfigEntries[key] = ss.getItem(key);
        }
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      if (key !== "siteConfig" && key.indexOf("siteConfig:") !== 0) {
        ss.removeItem(key);
      }
    });
    Object.keys(siteConfigEntries).forEach((key) => {
      if (siteConfigEntries[key] !== null) {
        ss.setItem(key, siteConfigEntries[key]);
      }
    });
  } catch (e) {
    // 忽略清理异常
  }
}

// 退出登录时统一清理浏览器缓存
export function clearStorageForLogout() {
  clearLocalStorageExceptUserGuides();
  clearSessionStorageExceptSiteConfig();
  setNosugarSessionFlag(false);
}

export function clearStorageForBrowserLogin() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.clear();
  } catch (e) {
    // 忽略清理异常
  }

  try {
    window.sessionStorage.clear();
  } catch (e) {
    // 忽略清理异常
  }
}

// hash 路由项目统一跳登录页，避免出现 /login#/login 这类真实路径和 hash 路径叠加
export function redirectToLogin() {
  if (typeof window === "undefined" || !window.location) {
    return;
  }
  const baseUrl = `${window.location.origin}${process.env.BASE_URL || "/"}`;
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  window.location.replace(`${normalizedBaseUrl}#/login`);
}

export function clearStorageAndRedirectToLogin() {
  clearStorageForLogout();
  redirectToLogin();
}

export function saveRedirectUrl(route) {
  if (typeof window === "undefined" || !window.localStorage || !route) {
    return;
  }

  try {
    window.localStorage.setItem("redirectUrl", JSON.stringify(route));
  } catch (e) {
    // 忽略存储异常
  }
}

export function getRedirectUrl() {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  try {
    const redirectUrl = window.localStorage.getItem("redirectUrl");
    return redirectUrl ? JSON.parse(redirectUrl) : null;
  } catch (e) {
    return null;
  }
}

export function consumeRedirectUrl() {
  const redirectUrl = getRedirectUrl();
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem("redirectUrl");
  }
  return redirectUrl;
}

function getAuthBootstrapPayload() {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  const accessToken = window.localStorage.getItem("t_k");
  if (!accessToken) {
    return null;
  }

  return {
    t_k: accessToken,
    token: window.localStorage.getItem("token") || "",
    roles: window.localStorage.getItem("roles") || "",
    time: window.localStorage.getItem("time") || "",
    nosugarSession: hasNosugarSessionFlag(),
  };
}

function buildAuthBootstrapValue() {
  const payload = getAuthBootstrapPayload();
  if (!payload) {
    return "";
  }

  return utf8ToBase64(JSON.stringify(payload));
}

function setHashRouteQueryParam(url, key, value) {
  const currentHash = url.hash || "";
  if (!currentHash.startsWith("#/")) {
    return url.toString();
  }

  const hashBody = currentHash.slice(1);
  const [routePath, queryString = ""] = hashBody.split("?");
  const params = new URLSearchParams(queryString);
  params.set(key, value);

  const nextQuery = params.toString();
  url.hash = nextQuery ? `#${routePath}?${nextQuery}` : `#${routePath}`;
  return url.toString();
}

export function appendAuthBootstrapToUrl(url) {
  if (typeof window === "undefined" || !url) {
    return url;
  }

  const bootstrapValue = buildAuthBootstrapValue();
  if (!bootstrapValue) {
    console.log("[认证接力] 无登录态，跳过追加");
    return url;
  }

  try {
    const resolved = new URL(url, window.location.href);
    const isSameOrigin = resolved.origin === window.location.origin;
    const isSpaHashRoute = !!resolved.hash && resolved.hash.startsWith("#/");
    const shouldAppend = isSameOrigin && isSpaHashRoute && hasNosugarSessionFlag();

    console.log("[认证接力] URL 检查", {
      原始URL: url,
      同源: isSameOrigin,
      是Hash路由: isSpaHashRoute,
      无糖会话: hasNosugarSessionFlag(),
      是否追加: shouldAppend
    });

    if (!shouldAppend) {
      return url;
    }

    const finalUrl = setHashRouteQueryParam(
      resolved,
      AUTH_BOOTSTRAP_QUERY_KEY,
      bootstrapValue,
    );

    console.log("[认证接力] ✅ 已追加认证参数", {
      最终URL: finalUrl
    });

    return finalUrl;
  } catch (e) {
    console.warn("[认证接力] URL 解析失败:", e);
    return url;
  }
}

export function hasAuthBootstrapQuery(route) {
  return !!(route && route.query && route.query[AUTH_BOOTSTRAP_QUERY_KEY]);
}

export function getQueryWithoutAuthBootstrap(query) {
  if (!query || !Object.prototype.hasOwnProperty.call(query, AUTH_BOOTSTRAP_QUERY_KEY)) {
    return query || {};
  }

  const nextQuery = { ...query };
  delete nextQuery[AUTH_BOOTSTRAP_QUERY_KEY];
  return nextQuery;
}

export function restoreAuthDataFromRouteQuery(route) {
  if (
    typeof window === "undefined" ||
    !window.localStorage ||
    !route ||
    !route.query
  ) {
    return false;
  }

  const rawValue = route.query[AUTH_BOOTSTRAP_QUERY_KEY];
  if (!rawValue || typeof rawValue !== "string") {
    return false;
  }

  console.log("[认证接力] 检测到认证参数，尝试恢复登录态");

  try {
    const decoded = base64ToUtf8(rawValue);
    const payload = decoded ? JSON.parse(decoded) : null;
    if (!payload || !payload.t_k) {
      console.warn("[认证接力] 解析失败或缺少 token");
      return false;
    }

    console.log("[认证接力] 恢复登录态", {
      有token: !!payload.t_k,
      有roles: !!payload.roles,
      无糖会话: !!payload.nosugarSession
    });

    window.localStorage.setItem("t_k", payload.t_k);
    window.localStorage.setItem(
      "token",
      payload.token || JSON.stringify({ accessToken: payload.t_k }),
    );

    if (payload.roles) {
      window.localStorage.setItem("roles", payload.roles);
    }
    if (payload.time) {
      window.localStorage.setItem("time", payload.time);
    } else {
      window.localStorage.setItem("time", String(Date.now()));
    }

    setNosugarSessionFlag(!!payload.nosugarSession);
    console.log("[认证接力] ✅ 登录态恢复成功");
    return true;
  } catch (e) {
    console.error("[认证接力] 恢复失败:", e);
    return false;
  }
}

/**
 * 从登录接口响应或 token 值中读取 accessToken。
 * @param {Object|string} data - 登录接口 data、token 对象或 token 字符串
 * @returns {string} accessToken，不存在时返回空字符串
 */
export function getAccessTokenFromAuthData(data) {
  if (!data) return "";
  const token = Object.prototype.hasOwnProperty.call(data, "token")
    ? data.token
    : data;
  if (typeof token === "string") return token;
  return token && token.accessToken ? token.accessToken : "";
}

/**
 * 统一存储登录认证数据到 localStorage
 * 供 Login.vue / request.js 401 续期等场景复用
 * @param {Object} data - 登录接口返回的 data 对象，格式 { token: { accessToken, refreshToken }, roles, user }
 * @returns {boolean} 是否存储成功
 */
export function storeAuthData(data) {
  if (!data || !data.token) return false;
  const accessToken = getAccessTokenFromAuthData(data);
  if (!accessToken) return false;
  const previousAccessToken = localStorage.getItem("t_k") || "";

  const tokenToStore =
    typeof data.token === "string" ? { accessToken } : { ...data.token };

  // 批量更新 localStorage，触发其他 tab 的 storage 事件
  const timestamp = Date.now();
  localStorage.setItem("time", String(timestamp));
  localStorage.setItem("token", JSON.stringify(tokenToStore));
  localStorage.setItem("t_k", accessToken);

  const roles = Array.isArray(data.roles)
    ? data.roles
    : Array.isArray(data.user && data.user.roles)
      ? data.user.roles
      : null;

  // 如果 roles 存在则存储，否则设置默认角色避免 undefined 导致 JSON.parse 错误
  if (roles && roles.length > 0) {
    localStorage.setItem("roles", JSON.stringify([...roles]));
  } else {
    console.warn("登录数据中缺少 roles 字段，使用默认角色");
    localStorage.setItem("roles", JSON.stringify([{ label: "initial_role", weight: 0 }]));
  }

  // 存储用户信息
  if (data.user) {
    localStorage.setItem("User", JSON.stringify(data.user));
  }

  // storage events only reach other tabs. Notify the current tab as well so
  // long-lived connections can replace credentials immediately after renewal.
  if (
    previousAccessToken &&
    previousAccessToken !== accessToken &&
    typeof window !== "undefined" &&
    typeof window.CustomEvent === "function"
  ) {
    window.dispatchEvent(
      new CustomEvent("tokenSynced", {
        detail: { key: "t_k", newValue: accessToken },
      }),
    );
  }

  return true;
}
